/* Amborella Organics — global behaviour (progressive enhancement only). */
(() => {
  const theme = window.theme || {};

  /* ---------- Utilities ---------- */
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const toast = (message) => {
    const region = document.querySelector('[data-toast-region]');
    if (!region || !message) return;
    const el = document.createElement('div');
    el.className = 'toast';
    el.textContent = message;
    region.replaceChildren(el);
    setTimeout(() => el.remove(), 3500);
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  theme.utils = { debounce, toast, parseHTML };

  /* ---------- Dialogs (drawers, search, filters) ---------- */
  const openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    document.querySelectorAll(`[aria-controls="${dialog.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    dialog.dispatchEvent(new CustomEvent('dialog:open'));
  };

  const closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    dialog.close();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialogOpen);
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        openDialog(dialog, opener);
      }
      return;
    }
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      event.preventDefault();
      closeDialog(closer.closest('dialog'));
      return;
    }
    // Backdrop click closes a modal dialog.
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) closeDialog(event.target);
    }
  });

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement)) return;
      document.querySelectorAll(`[aria-controls="${dialog.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (dialog._opener && document.contains(dialog._opener)) dialog._opener.focus({ preventScroll: true });
    },
    true
  );

  theme.openDialog = openDialog;
  theme.closeDialog = closeDialog;

  /* ---------- Disclosures (details elements that close on outside click / Escape) ---------- */
  document.addEventListener('click', (event) => {
    document.querySelectorAll('details[data-disclosure][open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = event.target.closest && event.target.closest('details[data-disclosure][open]');
    const target = open || document.querySelector('details[data-disclosure][open]');
    if (target) {
      target.removeAttribute('open');
      const summary = target.querySelector('summary');
      if (summary) summary.focus();
    }
  });

  /* ---------- Header ---------- */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.menus = Array.from(this.querySelectorAll('details[data-header-menu]'));
      this.menus.forEach((details) => {
        const item = details.closest('li') || details;
        const summary = details.querySelector('summary');
        item.addEventListener('mouseenter', () => {
          if (this.open(details)) details.dataset.hoverOpen = 'true';
        });
        item.addEventListener('mouseleave', () => {
          delete details.dataset.hoverOpen;
          details.removeAttribute('open');
        });
        if (summary) {
          summary.addEventListener('click', (event) => {
            if (details.dataset.hoverOpen) {
              event.preventDefault();
              delete details.dataset.hoverOpen;
            }
          });
        }
        details.addEventListener('toggle', () => {
          if (details.open) this.menus.forEach((other) => other !== details && other.removeAttribute('open'));
        });
      });
      this.setHeight = this.setHeight.bind(this);
      this.setHeight();
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.setHeight);
        this.observer.observe(this);
      }
      window.addEventListener('scroll', this.onScroll = () => {
        this.classList.toggle('is-scrolled', window.scrollY > 4);
      }, { passive: true });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('scroll', this.onScroll);
    }

    open(details) {
      if (!window.matchMedia('(hover: hover)').matches) return false;
      details.setAttribute('open', '');
      return true;
    }

    setHeight() {
      const height = `${Math.round(this.getBoundingClientRect().height)}px`;
      document.documentElement.style.setProperty('--header-height', height);
      document.documentElement.style.setProperty('--sticky-offset', this.classList.contains('site-header--sticky') ? height : '0px');
    }
  }
  if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);

  /* ---------- Cart ---------- */
  const cartSections = () => {
    const ids = ['cart-icon-bubble'];
    if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
    document.querySelectorAll('cart-items[data-section-id]').forEach((el) => ids.push(el.dataset.sectionId));
    return [...new Set(ids)];
  };

  const renderSections = (sections) => {
    if (!sections) return;
    const activeId = document.activeElement && document.activeElement.id;
    const activeDialog = document.activeElement && document.activeElement.closest && document.activeElement.closest('dialog[open]');
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = parseHTML(html);
      document.querySelectorAll(`[data-rendered-section="${id}"]`).forEach((target) => {
        const source = doc.querySelector(`[data-rendered-section="${id}"]`);
        if (source) target.replaceWith(document.importNode(source, true));
      });
    });
    if (activeId && !document.activeElement.id) {
      const next = document.getElementById(activeId);
      if (next) next.focus({ preventScroll: true });
    }
    // Focus was on something that got replaced (the drawer re-rendered under the keyboard): keep it in the dialog.
    if (activeDialog && activeDialog.open && !activeDialog.contains(document.activeElement)) {
      const fallback = activeDialog.querySelector('[data-dialog-close]');
      if (fallback) fallback.focus({ preventScroll: true });
    }
  };

  const cartRequest = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: body instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body instanceof FormData ? body : JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      const message = data.description || data.message || theme.strings.cartError;
      throw new Error(typeof message === 'string' ? message : theme.strings.cartError);
    }
    return data;
  };

  const afterCartChange = (data, { open = true } = {}) => {
    renderSections(data.sections);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    const drawer = document.getElementById('CartDrawer');
    if (open) {
      if (drawer && theme.cartType === 'drawer') {
        openDialog(drawer);
      } else if (!document.querySelector('cart-items[data-page]')) {
        window.location.href = theme.routes.cart;
      }
    }
  };

  let cartQueue = Promise.resolve();
  const enqueue = (task) => {
    cartQueue = cartQueue.catch(() => {}).then(task);
    return cartQueue;
  };

  theme.cart = {
    async add(formData) {
      formData.append('sections', cartSections().join(','));
      formData.append('sections_url', window.location.pathname);
      const data = await cartRequest(`${theme.routes.cartAdd}.js`, formData);
      afterCartChange(data);
      return data;
    },
    async addItems(items, { open = true } = {}) {
      const data = await cartRequest(`${theme.routes.cartAdd}.js`, {
        items,
        sections: cartSections().join(','),
        sections_url: window.location.pathname
      });
      afterCartChange(data, { open });
      return data;
    },
    change(key, quantity) {
      return enqueue(async () => {
        const data = await cartRequest(`${theme.routes.cartChange}.js`, {
          id: key,
          quantity,
          sections: cartSections().join(','),
          sections_url: window.location.pathname
        });
        afterCartChange(data, { open: false });
        return data;
      });
    },
    async note(note) {
      return cartRequest(`${theme.routes.cartUpdate}.js`, { note });
    },
    /* Re-render the drawer, cart page and count after something outside the theme (an app) changed the cart. */
    refresh({ open = false } = {}) {
      return enqueue(async () => {
        const ids = cartSections();
        const response = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`); // no Accept: json header: product URLs would answer with the product JSON instead
        if (!response.ok) throw new Error(`Cart sections request failed (${response.status})`);
        const sections = await response.json();
        afterCartChange({ sections }, { open });
        return sections;
      });
    }
  };

  /* Apps such as ShipAid add lines with their own requests and then announce it with `cart:refresh`
     (and open the drawer by clicking the header cart icon), so the drawer must not show stale lines. */
  document.addEventListener('cart:refresh', () => {
    document.querySelectorAll('#CartDrawer cart-items, cart-items[data-page]').forEach((el) => el.classList.add('is-updating'));
    theme.cart.refresh().catch(() => {
      document.querySelectorAll('cart-items.is-updating').forEach((el) => el.classList.remove('is-updating'));
    });
  });

  /* Product forms (PDP and quick add) */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy) return;
      const submitter = event.submitter || this.form.querySelector('[type="submit"]');
      if (submitter && submitter.getAttribute('aria-disabled') === 'true') return;
      this.busy = true;
      const formData = new FormData(this.form);
      if (submitter && submitter.name === 'id' && submitter.value) formData.set('id', submitter.value);
      this.setError('');
      submitter && submitter.classList.add('is-loading');
      submitter && submitter.setAttribute('aria-busy', 'true');
      try {
        await theme.cart.add(formData);
        const details = this.closest('details');
        if (details) details.removeAttribute('open');
        if (!document.getElementById('CartDrawer')) toast(theme.strings.added);
      } catch (error) {
        this.setError(error.message);
      } finally {
        this.busy = false;
        submitter && submitter.classList.remove('is-loading');
        submitter && submitter.removeAttribute('aria-busy');
      }
    }

    setError(message) {
      const box = this.querySelector('[data-form-error]');
      if (!box) {
        if (message) toast(message);
        return;
      }
      box.textContent = message;
      box.hidden = !message;
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* Cart line items (drawer and page) */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        this.update(remove.dataset.key, 0);
        return;
      }
      const step = event.target.closest('[data-qty-step]');
      if (step) {
        event.preventDefault();
        const input = step.parentElement.querySelector('input');
        const increment = Number(input.step) || 1;
        const next = Math.max(0, Number(input.value) + Number(step.dataset.qtyStep) * increment);
        input.value = next;
        this.update(input.dataset.key, next);
      }
    }

    onChange(event) {
      const input = event.target.closest('input[data-key]');
      if (input) this.update(input.dataset.key, Math.max(0, Number(input.value)));
      if (event.target.matches('[name="note"]')) theme.cart.note(event.target.value);
    }

    update(key, quantity) {
      this.pending = this.pending || new Map();
      clearTimeout(this.pending.get(key));
      this.pending.set(
        key,
        setTimeout(async () => {
          this.pending.delete(key);
          this.classList.add('is-updating');
          try {
            await theme.cart.change(key, quantity);
          } catch (error) {
            toast(error.message);
          } finally {
            this.classList.remove('is-updating');
          }
        }, 300)
      );
    }
  }
  if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);

  /* Delivery guarantee tick box in the cart (snippets/cart-protection.liquid). It adds or removes the
     guarantee product; the drawer and cart page then re-render with the new total. An element that only
     carries data-orphan-key means the guarantee is all that's left in the cart, so it removes it. */
  class CartProtection extends HTMLElement {
    connectedCallback() {
      if (this.dataset.orphanKey) {
        // The drawer and the cart page can both carry one; remove the line once.
        if (CartProtection.removing) return;
        CartProtection.removing = true;
        theme.cart
          .change(this.dataset.orphanKey, 0)
          .catch(() => {})
          .finally(() => {
            CartProtection.removing = false;
          });
        return;
      }
      this.input = this.querySelector('input[type="checkbox"]');
      if (this.input) this.input.addEventListener('change', () => this.toggle());
    }

    async toggle() {
      if (this.busy) return;
      const on = this.input.checked;
      // Not `disabled`: a disabled box drops keyboard focus; the re-render puts focus back on the new box by id.
      this.busy = true;
      this.classList.add('is-updating');
      this.setAttribute('aria-busy', 'true');
      try {
        if (on) {
          await theme.cart.addItems([{ id: Number(this.dataset.variantId), quantity: 1 }], { open: false });
        } else if (this.dataset.lineKey) {
          await theme.cart.change(this.dataset.lineKey, 0);
        }
      } catch (error) {
        this.input.checked = !on;
        toast(error.message);
      } finally {
        // On success the section re-renders and replaces this element; otherwise hand the box back.
        this.busy = false;
        this.classList.remove('is-updating');
        this.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('cart-protection')) customElements.define('cart-protection', CartProtection);

  /* ---------- Sliders ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      this.prevs = Array.from(this.querySelectorAll('[data-slider-prev]'));
      this.nexts = Array.from(this.querySelectorAll('[data-slider-next]'));
      this.prevs.forEach((button) => button.addEventListener('click', () => this.go(-1)));
      this.nexts.forEach((button) => button.addEventListener('click', () => this.go(1)));
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', debounce(this.update, 60), { passive: true });
      window.addEventListener('resize', debounce(this.update, 120));
      this.update();
    }

    go(direction) {
      const slide = this.track.firstElementChild;
      if (!slide) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = slide.getBoundingClientRect().width + gap;
      const visible = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
      this.track.scrollBy({ left: direction * step * visible, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      const scrollable = max > 2;
      this.classList.toggle('is-static', !scrollable);
      this.prevs.forEach((button) => {
        button.disabled = !scrollable || this.track.scrollLeft <= 2;
      });
      this.nexts.forEach((button) => {
        button.disabled = !scrollable || this.track.scrollLeft >= max;
      });
    }
  }
  if (!customElements.get('slider-component')) customElements.define('slider-component', SliderComponent);

  /* ---------- Product recommendations (lazy) ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      const load = async () => {
        try {
          const response = await fetch(this.dataset.url);
          const doc = parseHTML(await response.text());
          const content = doc.querySelector('[data-carousel]');
          if (content) this.replaceWith(content);
          else this.remove();
        } catch (error) {
          this.remove();
        }
      };
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            load();
          }
        }, { rootMargin: '0px 0px 600px 0px' });
        observer.observe(this);
      } else {
        load();
      }
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results || this.dataset.enabled !== 'true') return;
      this.input.addEventListener('input', debounce(() => this.search(), 280));
    }

    async search() {
      const term = this.input.value.trim();
      if (term.length < 2) {
        if (this.controller) this.controller.abort();
        this.results.innerHTML = '';
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: term,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last'
      });
      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!response.ok) return;
        const doc = parseHTML(await response.text());
        if (this.input.value.trim() !== term) return;
        const content = doc.querySelector('[data-predictive-content]');
        this.results.innerHTML = content ? content.innerHTML : '';
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Reduced motion: stop autoplaying video ---------- */
  const pauseMotion = () => {
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.querySelectorAll('video[autoplay]').forEach((video) => {
      video.pause();
      video.removeAttribute('autoplay');
      video.controls = true;
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', pauseMotion);
  else pauseMotion();

  /* ---------- Theme editor ---------- */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:select', (event) => {
      const drawer = event.target.querySelector('dialog.drawer');
      if (drawer && drawer.id === 'CartDrawer') openDialog(drawer);
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      const drawer = event.target.querySelector('dialog[open]');
      if (drawer) closeDialog(drawer);
    });
  }

  window.theme = theme;
})();
